import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Briefcase, Star, MapPin } from 'lucide-react';
import { useConnectionSuggestions, ConnectionSuggestion } from '@/features/home/hooks/useConnectionSuggestions';
import { useMySignals } from '@/features/sossial/hooks/useMySignals';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { getInitials } from '@/utils/avatarUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { useQueryClient } from '@tanstack/react-query';
import { connectionsRepository } from '@/features/home/repositories/supabaseConnectionsRepository';
import { SingleStepWizardModal, WizardStepType } from '@/components/sossial/SingleStepWizardModal';
import { EditLocationModal } from '@/features/profile/components/EditLocationModal';
import { CtaCard } from '@/components/discovery/CtaCard';
import { DiscoverySection } from '@/components/discovery/DiscoverySection';
import { bucketSuggestions } from '@/features/sossial/utils/connectionSuggestionFilters';

function SuggestionGridCard({
  suggestion,
  pending,
  onConnect,
  onProfile,
  connectLabel,
}: {
  suggestion: ConnectionSuggestion;
  pending: boolean;
  onConnect: (id: string) => void;
  onProfile: (username?: string) => void;
  connectLabel: string;
}) {
  return (
    <div className="bg-[#231a1a] rounded-xl p-4 flex flex-col items-center gap-2">
      <button onClick={() => onProfile(suggestion.username)}>
        <FramedAvatar
          src={suggestion.profile_picture_url}
          initials={getInitials(suggestion.full_name)}
          beltRank={suggestion.belt_rank || 'white'}
          size={32}
          frameSize={{ w: 48, h: 52 }}
          username={suggestion.username}
        />
      </button>
      <div className="text-center">
        <div className="flex items-center justify-center gap-1">
          <p className="text-[13px] font-semibold text-white leading-tight truncate max-w-[100px]">
            {suggestion.full_name}
          </p>
          {suggestion.is_premium && <PremiumBadge size="xs" />}
        </div>
        {suggestion.academy_name && (
          <p className="text-[11px] text-white/50 truncate max-w-[120px]">
            {suggestion.academy_name}
          </p>
        )}
      </div>
      <button
        onClick={() => onConnect(suggestion.id)}
        disabled={pending}
        className="w-full py-1.5 rounded-full text-[12px] font-semibold bg-sossiety-red text-white disabled:opacity-50"
      >
        {pending ? '···' : connectLabel}
      </button>
    </div>
  );
}

const ConnectionDiscover = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const { data: suggestions, isLoading } = useConnectionSuggestions();
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [signalsRefreshKey, setSignalsRefreshKey] = useState(0);
  const { signals } = useMySignals(user?.id, signalsRefreshKey);
  const [wizardType, setWizardType] = useState<WizardStepType | null>(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleConnect = async (userId: string) => {
    if (!user) return;
    setPendingIds(prev => new Set(prev).add(userId));
    try {
      await connectionsRepository.sendConnectionRequest(user.id, userId);
      setSentIds(prev => new Set(prev).add(userId));
      toast({ title: t('connection.request_sent'), description: t('connection.request_sent_desc') });
    } catch {
      toast({ title: t('connection.request_error'), variant: 'destructive' });
    } finally {
      setPendingIds(prev => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const handleSignalSaved = () => {
    setSignalsRefreshKey(k => k + 1);
    queryClient.invalidateQueries({ queryKey: ['connection-suggestions'] });
  };

  const handleLocationSave = async (data: { country: string; state: string; city: string }) => {
    if (!user) return;
    await connectionsRepository.updateLocation(user.id, data);
    setLocationOpen(false);
    handleSignalSaved();
  };

  const available = (suggestions ?? []).filter(s => !sentIds.has(s.id));
  const discoverySignals = signals
    ? {
        main_academy_id: signals.main_academy_id,
        profession: signals.profession,
        has_interests: signals.has_interests,
        hobby_ids: signals.hobby_ids ?? [],
        city: signals.city,
        state: signals.state,
        country: signals.country,
      }
    : null;
  const buckets = bucketSuggestions(available, discoverySignals);

  const renderGrid = (items: ConnectionSuggestion[]) => (
    <div className="grid grid-cols-2 gap-3">
      {items.map(suggestion => (
        <SuggestionGridCard
          key={suggestion.id}
          suggestion={suggestion}
          pending={pendingIds.has(suggestion.id)}
          onConnect={handleConnect}
          onProfile={username => username && navigate(`/profile/${username}`)}
          connectLabel={t('discovery.connect')}
        />
      ))}
    </div>
  );

  const emptyHint = (messageKey: string) => (
    <p className="text-[12px] text-sossiety-grey-300">{t(messageKey)}</p>
  );

  const hasAnySuggestions =
    buckets.peopleYouMightKnow.length > 0 ||
    buckets.gymMates.length > 0 ||
    buckets.sameProfession.length > 0 ||
    buckets.mutuals.length > 0 ||
    buckets.sameInterests.length > 0 ||
    buckets.nearYou.length > 0;

  return (
    <div className="min-h-screen bg-sossiety-dark pb-24">
      <div className="sticky top-0 z-10 flex items-center gap-2 px-3 h-14 bg-[rgba(22,12,12,0.92)] border-b border-white/[0.06]">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/5 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
        <span className="flex-1 text-center font-bold text-base text-white pr-10">
          {t('discovery.people_you_might_know')}
        </span>
      </div>

      <div className="px-4 pt-4 space-y-6">
        {isLoading && (
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-[#231a1a] rounded-xl p-4 animate-pulse h-40" />
            ))}
          </div>
        )}

        {!isLoading && (
          <>
            {buckets.peopleYouMightKnow.length > 0 && (
              <DiscoverySection title={t('discovery.people_you_might_know')} scroll={false}>
                {renderGrid(buckets.peopleYouMightKnow)}
              </DiscoverySection>
            )}

            {!signals?.main_academy_id ? (
              <DiscoverySection title={t('discovery.gym_mates')} scroll={false}>
                <CtaCard
                  title={t('discovery.cta.add_academy_title')}
                  description={t('discovery.cta.add_academy_desc')}
                  action={t('discovery.cta.add_now')}
                  icon={<Users className="w-4 h-4" />}
                  onClick={() => setWizardType('academy')}
                />
              </DiscoverySection>
            ) : (
              <DiscoverySection title={t('discovery.gym_mates')} scroll={false}>
                {buckets.gymMates.length > 0
                  ? renderGrid(buckets.gymMates)
                  : emptyHint('discovery.no_gym_mates')}
              </DiscoverySection>
            )}

            {!signals?.profession ? (
              <DiscoverySection title={t('discovery.same_profession')} scroll={false}>
                <CtaCard
                  title={t('discovery.cta.add_profession_title')}
                  description={t('discovery.cta.add_profession_desc')}
                  action={t('discovery.cta.add_now')}
                  icon={<Briefcase className="w-4 h-4" />}
                  onClick={() => setWizardType('profession')}
                />
              </DiscoverySection>
            ) : (
              <DiscoverySection title={t('discovery.same_profession')} scroll={false}>
                {buckets.sameProfession.length > 0
                  ? renderGrid(buckets.sameProfession)
                  : emptyHint('discovery.no_profession_matches')}
              </DiscoverySection>
            )}

            <DiscoverySection title={t('discovery.mutuals')} scroll={false}>
              {buckets.mutuals.length > 0
                ? renderGrid(buckets.mutuals)
                : emptyHint('discovery.no_mutual_matches')}
            </DiscoverySection>

            {!signals?.has_interests ? (
              <DiscoverySection title={t('discovery.same_interests')} scroll={false}>
                <CtaCard
                  title={t('discovery.cta.pick_interests_title')}
                  description={t('discovery.cta.pick_interests_desc')}
                  action={t('discovery.cta.pick_now')}
                  icon={<Star className="w-4 h-4" />}
                  onClick={() => setWizardType('interests')}
                />
              </DiscoverySection>
            ) : (
              <DiscoverySection title={t('discovery.same_interests')} scroll={false}>
                {buckets.sameInterests.length > 0
                  ? renderGrid(buckets.sameInterests)
                  : emptyHint('discovery.no_interest_matches')}
              </DiscoverySection>
            )}

            {!signals?.country ? (
              <DiscoverySection title={t('discovery.near_to_you')} scroll={false}>
                <CtaCard
                  title={t('discovery.cta.add_location_title')}
                  description={t('discovery.cta.add_location_desc')}
                  action={t('discovery.cta.add_now')}
                  icon={<MapPin className="w-4 h-4" />}
                  onClick={() => setLocationOpen(true)}
                />
              </DiscoverySection>
            ) : (
              <DiscoverySection title={t('discovery.near_to_you')} scroll={false}>
                {buckets.nearYou.length > 0
                  ? renderGrid(buckets.nearYou)
                  : emptyHint('discovery.no_location_matches')}
              </DiscoverySection>
            )}

            {!hasAnySuggestions &&
              signals?.main_academy_id &&
              signals.profession &&
              signals.has_interests &&
              signals.country && (
              <div className="flex flex-col items-center justify-center min-h-[30vh] text-center">
                <p className="text-white/60 text-sm">{t('discovery.no_suggestions')}</p>
              </div>
            )}
          </>
        )}
      </div>

      <SingleStepWizardModal
        open={wizardType !== null}
        type={wizardType}
        onClose={() => setWizardType(null)}
        onSaved={handleSignalSaved}
      />
      <EditLocationModal
        open={locationOpen}
        onClose={() => setLocationOpen(false)}
        onSave={handleLocationSave}
        initialData={{ country: '', state: '', city: '' }}
      />
    </div>
  );
};

export default ConnectionDiscover;
